/* eslint-disable react-refresh/only-export-components */
import { Suspense, lazy, useEffect } from 'react';
import { createHashRouter, Navigate, useLocation } from 'react-router-dom';
import i18n from '@/lib/i18n';
import { LoginSkeleton } from '@/components/auth/LoginSkeleton';
import { RequireAuth, RequireAutopilotAccess, RequireGuest } from '@/components/auth/AuthGuards';
import StudioLayout from '@/layouts/StudioLayout';
import DashboardLayout from '@/layouts/DashboardLayout';
import UsageLayout from '@/layouts/UsageLayout';
import { CREDIT_PURCHASE_URL } from '@/lib/creditPurchase';

// 懒加载页面
const Create = lazy(() => import('@/pages/create/Create'));
const CreateFresh = lazy(() => import('@/pages/create/CreateFresh'));
const CreateSales = lazy(() => import('@/pages/create/CreateSales'));
const Login = lazy(() => import('@/pages/auth/Login'));
const OAuthCallback = lazy(() => import('@/pages/auth/OAuthCallback'));
const SignupSuccess = lazy(() => import('@/pages/auth/SignupSuccess'));
const Dashboard = lazy(() => import('@/pages/dashboard/Dashboard'));
const Help = lazy(() => import('@/pages/help/Help'));
const Autopilot = lazy(() => import('@/pages/autopilot/Autopilot'));
const UsageHistory = lazy(() => import('@/pages/usage/UsageHistory'));
const NotFound = lazy(() => import('@/pages/system/NotFound'));
const Studio = lazy(() => import('@/pages/studio/Studio'));
const ErrorPage = lazy(() => import('@/pages/system/ErrorPage'));
const CreationLayout = lazy(() => import('@/layouts/CreationLayout'));
const QuickWalkthrough = lazy(() => import('@/pages/walkthrough/QuickWalkthrough'));
const Diagnosis = lazy(() => import('@/pages/autopilot/AutopilotDiagnosisV2'));

const RedirectTo404 = () => {
  const location = useLocation();
  const from = `${location.pathname}${location.search}${location.hash}`;
  return <Navigate to="/404" replace state={{ from }} />;
};

const RedirectToPath = ({ to }: { to: string }) => {
  const location = useLocation();
  const from = `${location.pathname}${location.search}${location.hash}`;
  return <Navigate to={to} replace state={{ from }} />;
};

const RedirectToPricing = () => {
  useEffect(() => {
    window.location.replace(CREDIT_PURCHASE_URL);
  }, []);

  return null;
};

/**
 * 路由配置
 */
export const router = createHashRouter([
  {
    path: '/',
    element: <Navigate to="/create" replace />,
  },
  {
    path: '/walkthrough',
    element: (
      <Suspense fallback={null}>
        <QuickWalkthrough />
      </Suspense>
    ),
  },
  {
    path: '/create-v3',
    element: <RedirectToPath to="/create" />,
  },
  {
    path: '/create-v3-fresh',
    element: <RedirectToPath to="/create/fresh" />,
  },
  {
    path: '/create-v3-sales',
    element: <RedirectToPath to="/create/sales" />,
  },
  {
    path: '/credits',
    element: <RedirectToPricing />,
  },
  {
    path: '/credits/*',
    element: <RedirectToPricing />,
  },
  {
    path: '/diagnosis',
    element: (
      <Suspense fallback={null}>
        <Diagnosis />
      </Suspense>
    ),
  },
  {
    path: '/autopilot-diagnosis-v2',
    element: <Navigate to="/diagnosis" replace />,
  },
  {
    element: (
      <Suspense fallback={null}>
        <CreationLayout />
      </Suspense>
    ),
    children: [
      {
        path: '/create',
        element: (
          <Suspense
            fallback={
              <div className="text-muted-foreground flex min-h-screen animate-pulse items-center justify-center text-sm font-medium">
                Preparing...
              </div>
            }
          >
            <Create />
          </Suspense>
        ),
      },
      {
        path: '/create/fresh',
        element: (
          <Suspense
            fallback={
              <div className="text-muted-foreground flex min-h-screen animate-pulse items-center justify-center text-sm font-medium">
                Preparing...
              </div>
            }
          >
            <CreateFresh />
          </Suspense>
        ),
      },
      {
        path: '/create/sales',
        element: (
          <Suspense
            fallback={
              <div className="text-muted-foreground flex min-h-screen animate-pulse items-center justify-center text-sm font-medium">
                Preparing...
              </div>
            }
          >
            <CreateSales />
          </Suspense>
        ),
      },
    ],
  },

  // 需要认证的路由组
  {
    element: <RequireAutopilotAccess />,
    errorElement: (
      <Suspense fallback={null}>
        <ErrorPage />
      </Suspense>
    ),
    children: [
      {
        path: '/autopilot',
        element: (
          <Suspense fallback={null}>
            <Autopilot />
          </Suspense>
        ),
      },
    ],
  },
  {
    element: <RequireAuth />,
    errorElement: (
      <Suspense fallback={null}>
        <ErrorPage />
      </Suspense>
    ),
    children: [
      // Help 独立全屏页面，内部复刻原型顶栏与布局
      {
        path: '/help',
        element: (
          <Suspense
            fallback={
              <div className="text-muted-foreground flex min-h-screen animate-pulse items-center justify-center text-sm font-medium">
                Loading...
              </div>
            }
          >
            <Help />
          </Suspense>
        ),
      },
      {
        path: '/signup-success',
        element: (
          <Suspense fallback={null}>
            <SignupSuccess />
          </Suspense>
        ),
      },
      {
        element: <DashboardLayout />,
        children: [
          {
            path: '/dashboard',
            element: (
              <Suspense
                fallback={
                  <div className="text-muted-foreground flex min-h-[400px] animate-pulse items-center justify-center text-sm font-medium">
                    Loading...
                  </div>
                }
              >
                <Dashboard />
              </Suspense>
            ),
          },
        ],
      },
      {
        element: <UsageLayout />,
        children: [
          {
            path: '/usage',
            element: (
              <Suspense
                fallback={
                  <div className="text-muted-foreground flex min-h-[400px] animate-pulse items-center justify-center text-sm font-medium">
                    Loading...
                  </div>
                }
              >
                <UsageHistory />
              </Suspense>
            ),
          },
        ],
      },
      {
        element: <StudioLayout />,
        children: [
          {
            path: '/studio',
            element: (
              <Suspense
                fallback={
                  <div className="text-muted-foreground flex min-h-[400px] animate-pulse items-center justify-center text-sm font-medium">
                    Entering workspace...
                  </div>
                }
              >
                <Studio />
              </Suspense>
            ),
          },
        ],
      },
    ],
  },

  // 访客专属路由 (登录状态下访问会重定向)
  {
    element: <RequireGuest />,
    children: [
      {
        path: '/login',
        element: (
          <Suspense fallback={<LoginSkeleton />}>
            <Login />
          </Suspense>
        ),
      },
      {
        path: '/oauth/callback',
        element: (
          <Suspense fallback={null}>
            <OAuthCallback />
          </Suspense>
        ),
      },
    ],
  },

  // 404
  {
    path: '/404',
    element: (
      <Suspense
        fallback={
          <div className="text-muted-foreground flex min-h-screen animate-pulse items-center justify-center text-sm font-medium">
            {i18n.t('common:messages.loading')}
          </div>
        }
      >
        <NotFound />
      </Suspense>
    ),
  },
  {
    path: '*',
    element: <RedirectTo404 />,
  },
]);
